import { BillingTier, resolveBillingTier } from './plan-tier.util';

/**
 * Dashboard product features gated by license tier (SOP V3 pp. 9–10 + product nav).
 * Admin dashboard is separate ({@code ADMIN} role) and never granted by these tiers.
 */
export type DashboardFeature =
  | 'sevenDayPlan'
  | 'jobTracker'
  | 'linkedin'
  | 'interviewPrep'
  | 'outreach'
  | 'voiceNotes'
  | 'jobDescriptions'
  | 'cvOptimization';

/**
 * Window used to count **CV analysis** (resume uploads) and **complete analysis**.
 * Same clock rules for both; the caps differ (see {@link cvAnalysisLimitForTier}
 * vs {@link cvOptimizationLimitForTier}).
 */
export type OptimizationWindow = 'none' | 'licenseMonth' | 'calendarDay';

/**
 * Max original voice-note questions allowed per CV for the tier.
 * Free: none · Core: 4 · Pro: 8 guided notes · Premium: 10 questions.
 */
export function voiceNoteLimitForTier(tier: BillingTier): number {
  switch (tier) {
    case 'core':
      return 4;
    case 'pro':
      return 8;
    case 'premium':
      return 10;
    default:
      return 0;
  }
}

export function voiceNoteLimitFromRoles(roles: string[] | null | undefined): number {
  return voiceNoteLimitForTier(resolveBillingTier(roles));
}

/**
 * Max job descriptions allowed per CV.
 * Free: 0 · Core / Pro / Premium: 1.
 */
export function jobDescriptionsPerResumeLimitForTier(tier: BillingTier): number {
  switch (tier) {
    case 'core':
    case 'pro':
    case 'premium':
      return 1;
    default:
      return 0;
  }
}

/**
 * Max upload-time **CV analyses** (resume uploads) allowed in {@link cvAnalysisWindowForTier}.
 * Mirrors backend {@code LicenseTier.cvAnalysisLimit()}:
 * Free: unlimited · Core: 2 / license-month · Pro: 8 / license-month · Premium: 1 / day.
 * Separate from complete-analysis quota — an upload must not consume a complete analysis.
 */
export function cvAnalysisLimitForTier(tier: BillingTier): number {
  switch (tier) {
    case 'core':
      return 2;
    case 'pro':
      return 8;
    case 'premium':
      return 1;
    case 'free':
    default:
      return Number.POSITIVE_INFINITY;
  }
}

/** Window for CV analysis (upload) counting — same clock as complete analysis, different cap. */
export function cvAnalysisWindowForTier(tier: BillingTier): OptimizationWindow {
  return cvOptimizationWindowForTier(tier);
}

/**
 * Max **complete analyses** allowed in {@link cvOptimizationWindowForTier}.
 * Mirrors backend {@code LicenseTier.cvOptimizationLimit()} (complete-analysis pool — never uploads):
 * Free: 0 · Core: 2 / license-month · Pro: 8 / license-month · Premium: 1 / day.
 */
export function cvOptimizationLimitForTier(tier: BillingTier): number {
  switch (tier) {
    case 'core':
      return 2;
    case 'pro':
      return 8;
    case 'premium':
      return 1;
    default:
      return 0;
  }
}

export function cvOptimizationWindowForTier(tier: BillingTier): OptimizationWindow {
  switch (tier) {
    case 'core':
    case 'pro':
      return 'licenseMonth';
    case 'premium':
      return 'calendarDay';
    default:
      return 'none';
  }
}

/**
 * Start of the current analysis/rewrite usage window (UTC), as epoch millis.
 * When {@code licenseExpiresAtEpoch} + a positive duration are available, Core/Pro use
 * {@code expires - duration}; otherwise they fall back to the UTC calendar month.
 * Shared clock for both quotas; callers must count the correct entity (resumes vs complete analyses).
 */
export function optimizationWindowStartMs(
  tier: BillingTier,
  licenseExpiresAtEpoch: number | null | undefined,
  licenseDurationSeconds: number | null | undefined = 30 * 24 * 60 * 60,
): number {
  const window = cvOptimizationWindowForTier(tier);
  const now = Date.now();
  if (window === 'calendarDay') {
    const d = new Date(now);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  if (window === 'licenseMonth') {
    if (
      licenseExpiresAtEpoch != null &&
      licenseExpiresAtEpoch > 0 &&
      licenseDurationSeconds != null &&
      licenseDurationSeconds > 0
    ) {
      return Math.max(0, (licenseExpiresAtEpoch - licenseDurationSeconds) * 1000);
    }
    const d = new Date(now);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  }
  return 0;
}

/** Alias for analysis quota — same rules as {@link optimizationWindowStartMs}. */
export function analysisWindowStartMs(
  tier: BillingTier,
  licenseExpiresAtEpoch: number | null | undefined,
  licenseDurationSeconds: number | null | undefined = 30 * 24 * 60 * 60,
): number {
  return optimizationWindowStartMs(tier, licenseExpiresAtEpoch, licenseDurationSeconds);
}

/** Count ISO/date values whose instant is at or after {@code startMs}. */
export function countCreatedInWindow(
  createdAts: ReadonlyArray<string | number | Date | null | undefined>,
  startMs: number,
): number {
  let n = 0;
  for (const raw of createdAts) {
    if (raw == null) continue;
    const t = new Date(raw).getTime();
    if (!Number.isNaN(t) && t >= startMs) n += 1;
  }
  return n;
}

/**
 * True when the tier's **CV analysis** (upload) quota is exhausted for {@code usedInWindow}.
 * Free (unlimited) never exhausts. Does not consider complete-analysis usage.
 */
export function isCvAnalysisQuotaExhausted(
  tier: BillingTier,
  usedInWindow: number,
): boolean {
  const limit = cvAnalysisLimitForTier(tier);
  if (!Number.isFinite(limit)) return false;
  return usedInWindow >= limit;
}

/**
 * True when the tier's **complete analysis** quota is exhausted for {@code usedInWindow}.
 * Free (limit 0) is always exhausted. Does not consider upload/analysis usage.
 */
export function isCvRewriteQuotaExhausted(
  tier: BillingTier,
  usedInWindow: number,
): boolean {
  const limit = cvOptimizationLimitForTier(tier);
  if (!canAccessFeature(tier, 'cvOptimization') || limit <= 0) return true;
  if (!Number.isFinite(limit)) return false;
  return usedInWindow >= limit;
}

export function canAccessFeature(tier: BillingTier, feature: DashboardFeature): boolean {
  switch (feature) {
    case 'jobTracker':
    case 'voiceNotes':
    case 'jobDescriptions':
    case 'cvOptimization':
      return tier === 'core' || tier === 'pro' || tier === 'premium';
    case 'sevenDayPlan':
    case 'interviewPrep':
    case 'outreach':
      // Pro unlocks these blades. Premium includes Pro.
      return tier === 'premium' || tier === 'pro';
    case 'linkedin':
      // Premium-only (SOP / product: LinkedIn blade is not on Pro).
      return tier === 'premium';
    default:
      return false;
  }
}

export function canAccessFeatureFromRoles(
  roles: string[] | null | undefined,
  feature: DashboardFeature,
): boolean {
  return canAccessFeature(resolveBillingTier(roles), feature);
}

/** Minimum tier label for upgrade CTAs. */
export function requiredTierLabel(feature: DashboardFeature): string {
  switch (feature) {
    case 'jobTracker':
    case 'voiceNotes':
    case 'jobDescriptions':
    case 'cvOptimization':
      return 'Core';
    case 'sevenDayPlan':
    case 'interviewPrep':
    case 'outreach':
      return 'Pro';
    case 'linkedin':
      return 'Premium';
  }
}
